import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail, Gift } from "lucide-react";
import CancellationsCard from "./CancellationsCard";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const RANGES = [7, 30, 90];

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className="text-xl font-bold text-kilted-green">{value}</div>
  </div>
);

export default function InsightsTab() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestBusy, setDigestBusy] = useState(false);
  const [winbackBusy, setWinbackBusy] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/insights?days=${days}`).then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [days]);

  const emailDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/admin/insights/digest");
      toast.success(`Snapshot emailed to ${data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the digest"); }
    finally { setDigestBusy(false); }
  };

  const winBack = async (l) => {
    if (!window.confirm(`Send ${l.name} a "we miss you" email with account credit?`)) return;
    setWinbackBusy(l.customer_id);
    try {
      const { data } = await api.post(`/admin/insights/winback/${l.customer_id}`);
      toast.success(`Win-back sent to ${data.email} with $${data.amount.toFixed(2)} credit`);
      api.get(`/admin/insights?days=${days}`).then((r) => setD(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the win-back"); }
    finally { setWinbackBusy(""); }
  };

  if (loading && !d) return <div className="mt-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-kilted-green" /></div>;
  if (!d) return <p className="mt-6 text-sm text-muted-foreground">Could not load insights.</p>;

  const heatMax = Math.max(1, ...d.heatmap.flat());
  const funnelMax = Math.max(1, ...d.funnel.map((f) => f.sessions));
  const mixRow = (mix) => Object.entries(mix).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
    <span key={k} className="mr-2 inline-block rounded-full bg-secondary px-3 py-1 text-xs font-semibold capitalize">{k} · {v}</span>
  ));

  return (
    <div className="mt-4 space-y-4" data-testid="insights-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2 rounded-lg border border-kilted-green/20 bg-kilted-green/5 p-3 text-xs text-muted-foreground" data-testid="insights-privacy-notice">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-kilted-green" />
          <span><strong className="text-kilted-green">Anonymous by design.</strong> Interaction events store only a random session id, role, device type and the action taken — never names, emails or account ids — and auto-delete after 12 months. Trends below are aggregates from your own order records; customers are shown by number (#) where possible.</span>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <Button key={r} size="sm" variant={days === r ? "default" : "outline"} onClick={() => setDays(r)}
              data-testid={`insights-range-${r}`} className={days === r ? "bg-kilted-green hover:bg-kilted-greenLight" : ""}>
              {r}d
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={emailDigest} disabled={digestBusy} data-testid="insights-digest-button" className="ml-2 gap-1.5">
            {digestBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Email me this snapshot
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card data-testid="insights-funnel-card">
          <CardHeader><CardTitle className="text-lg">Booking funnel</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {d.funnel.map((f) => (
              <div key={f.stage} data-testid={`funnel-${f.stage.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="mb-1 flex justify-between text-sm">
                  <span className="font-medium">{f.stage}</span>
                  <span className="text-muted-foreground">{f.sessions} session{f.sessions === 1 ? "" : "s"} · {f.pct}%</span>
                </div>
                <div className="h-3 rounded-full bg-secondary">
                  <div className="h-3 rounded-full bg-kilted-green transition-all" style={{ width: `${(f.sessions / funnelMax) * 100}%` }} />
                </div>
              </div>
            ))}
            {d.event_count === 0 && <p className="text-xs text-muted-foreground">No interaction events yet — they'll start flowing as customers use the site.</p>}
            <div className="flex gap-6 pt-1 text-xs text-muted-foreground">
              <span>Devices: {Object.entries(d.devices).map(([k, v]) => `${k} ${v}`).join(" · ") || "—"}</span>
              <span>Payment cancels: {d.features.payment_cancel}</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="insights-heatmap-card">
          <CardHeader><CardTitle className="text-lg">When bookings happen <span className="text-xs font-normal text-muted-foreground">(Sydney time)</span></CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {d.heatmap.map((row, i) => (
                <div key={DOW[i]} className="flex items-center gap-1">
                  <span className="w-8 text-[10px] font-semibold text-muted-foreground">{DOW[i]}</span>
                  {row.map((v, h) => (
                    <div key={h} title={`${DOW[i]} ${h}:00 — ${v} booking${v === 1 ? "" : "s"}`}
                      className="h-4 flex-1 rounded-sm"
                      style={{ backgroundColor: v ? `rgba(27, 67, 50, ${0.15 + 0.85 * (v / heatMax)})` : "rgba(0,0,0,0.05)" }} />
                  ))}
                </div>
              ))}
              <div className="flex justify-between pl-9 text-[9px] text-muted-foreground"><span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span></div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="insights-routes-card">
          <CardHeader><CardTitle className="text-lg">Popular routes & mix</CardTitle></CardHeader>
          <CardContent>
            {d.top_routes.length === 0 ? <p className="text-sm text-muted-foreground">No bookings in this window.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Route</TableHead><TableHead className="text-right">Bookings</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.top_routes.map((r) => (
                    <TableRow key={r.route}><TableCell className="font-medium">{r.route}</TableCell><TableCell className="text-right">{r.count}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="mt-3 space-y-2 text-sm">
              <div><span className="text-xs font-semibold uppercase text-muted-foreground">Sizes:</span> <span className="ml-1">{mixRow(d.size_mix)}</span></div>
              <div><span className="text-xs font-semibold uppercase text-muted-foreground">Service:</span> <span className="ml-1">{mixRow(d.service_mix)}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="insights-rhythm-card">
          <CardHeader><CardTitle className="text-lg">Customer rhythm</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-6">
              <Stat label="Customers with orders" value={d.rhythm.customers} testid="rhythm-customers" />
              <Stat label="Repeat rate" value={`${d.rhythm.repeat_rate_pct}%`} testid="rhythm-repeat-rate" />
              <Stat label="Avg days between bookings" value={d.rhythm.avg_days_between ?? "—"} testid="rhythm-avg-days" />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Gone quiet (3+ deliveries, nothing in 30 days)</p>
              {d.rhythm.lapsed.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="lapsed-empty">No lapsed regulars — nice.</p> : (
                <div className="space-y-1" data-testid="lapsed-list">
                  {d.rhythm.lapsed.map((l) => {
                    const recent = l.winback_sent_at && Date.now() - new Date(l.winback_sent_at).getTime() < 90 * 864e5;
                    return (
                    <div key={l.customer_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-amber-50 px-3 py-1.5 text-sm">
                      <span className="font-medium">{l.customer_no ? `#${l.customer_no} · ` : ""}{l.name}</span>
                      <span className="flex items-center gap-2 text-muted-foreground">
                        {l.orders} orders · last {l.last_order}
                        <Button size="sm" variant="outline" onClick={() => winBack(l)} disabled={winbackBusy === l.customer_id || recent}
                          data-testid={`winback-button-${l.customer_id}`} className="h-7 gap-1 px-2 text-xs">
                          {winbackBusy === l.customer_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Gift className="h-3 w-3" />}
                          {recent ? `Sent ${l.winback_sent_at}` : "Win back"}
                        </Button>
                      </span>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="insights-abandoned-card">
          <CardHeader><CardTitle className="text-lg">Abandoned quotes <span className="text-xs font-normal text-muted-foreground">(priced but never paid, &gt;2h old)</span></CardTitle></CardHeader>
          <CardContent>
            {d.quote_shares && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-kilted-gold/10 px-3 py-2 text-sm" data-testid="quote-share-metric">
                <span className="text-xs font-semibold uppercase text-kilted-green">Shared quotes (SMS)</span>
                <span>{d.quote_shares.sent} sent</span>
                <span className="text-muted-foreground">·</span>
                <span><b>{d.quote_shares.booked}</b> booked ({d.quote_shares.pct}%)</span>
                {d.quote_shares.value_booked > 0 && (<><span className="text-muted-foreground">·</span><span className="font-semibold text-kilted-green">${d.quote_shares.value_booked.toFixed(2)} booked value</span></>)}
              </div>
            )}
            {d.nudges && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-kilted-green/5 px-3 py-2 text-sm" data-testid="nudge-performance">
                <span className="text-xs font-semibold uppercase text-kilted-green">Nudge emails</span>
                <span>{d.nudges.sent} sent</span>
                <span className="text-muted-foreground">·</span>
                <span><b>{d.nudges.converted}</b> converted to paid ({d.nudges.pct}%)</span>
                {d.nudges.recovered_value > 0 && (<><span className="text-muted-foreground">·</span><span className="font-semibold text-kilted-green">${d.nudges.recovered_value.toFixed(2)} recovered</span></>)}
              </div>
            )}
            {d.abandoned.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="abandoned-empty">Nothing left on the table.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Tracking</TableHead><TableHead>Customer</TableHead><TableHead className="text-right">Value</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.abandoned.map((a) => (
                    <TableRow key={a.tracking_number} data-testid={`abandoned-${a.tracking_number}`}>
                      <TableCell className="text-xs">{a.date}</TableCell>
                      <TableCell className="font-medium">{a.tracking_number}</TableCell>
                      <TableCell>{a.customer_name}</TableCell>
                      <TableCell className="text-right font-semibold">${Number(a.total || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <CancellationsCard c={d.cancellations} />

        <Card data-testid="insights-drivers-card">
          <CardHeader><CardTitle className="text-lg">Driver patterns</CardTitle></CardHeader>
          <CardContent>
            {d.drivers.length === 0 ? <p className="text-sm text-muted-foreground">No completed stops in this window.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Driver</TableHead><TableHead className="text-right">Stops</TableHead><TableHead className="text-right">Avg pickup→drop</TableHead><TableHead className="text-right">Stops/day</TableHead><TableHead className="text-right">Busiest hour</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.drivers.map((dr) => (
                    <TableRow key={dr.name} data-testid={`driver-pattern-${dr.name.replace(/\s+/g, "-")}`}>
                      <TableCell className="font-medium">{dr.name}</TableCell>
                      <TableCell className="text-right">{dr.stops}</TableCell>
                      <TableCell className="text-right">{dr.avg_mins_to_deliver != null ? `${dr.avg_mins_to_deliver} min` : "—"}</TableCell>
                      <TableCell className="text-right">{dr.avg_stops_per_day}</TableCell>
                      <TableCell className="text-right">{dr.busiest_hour != null ? `${dr.busiest_hour}:00` : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>Tracking page views: {d.features.tracking_viewed}</span>
              <span>Invoice downloads: {d.features.invoice_downloaded}</span>
              <span>Share clicks: {d.features.share_clicked}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
